import { actionTypes, browserCategories } from './constants';
import { apiCallStatus } from 'helpers/constants';
import { getApiActionCreator } from 'helpers/actionHelper';
import { TimeSeriesApi, AnalyticsApi, CapabilityApi, ConditionIndicesApi, SubscriptionInfoApi } from 'api';
import { getCrossAssetAnalysisActiveTab } from '../../PowertrainAssetDetails/CrossAssetAnalysis/CrossAssetAnalysisSidepanel/selectors';
import { opButtons } from '../../PowertrainAssetDetails/CrossAssetAnalysis/ChartView/constants';
import { formatConditionIndexOverviewRequest } from '../ConditionIndicesOverviewV2/helpers';

const getKPIsAction = getApiActionCreator(actionTypes.GET_KPIS_FOR_KPI_TYPE);
const getCapabilityAction = getApiActionCreator(actionTypes.GET_ASSET_CAPABILITY);
const getSubscriptionAction = getApiActionCreator(actionTypes.GET_ASSET_SUBSCRIPTION);
const getPowertrainListForBrowserAction = getApiActionCreator(actionTypes.GET_POWERTRAIN_LIST_FOR_BROWSER);

const clearKpiList = () => ({ type: actionTypes.CLEAR_KPI_LIST });
export const clearOffGraphSignals = (tabPrefix) => ({ type: actionTypes.CLEAR_OFF_GRAPH_SIGNALS, tabPrefix });

export const addOffGraphSignal = (signal) => ({
    type: actionTypes.ADD_OFF_GRAPH_SIGNAL,
    signal
});

export const removeOffGraphSignal = (key) => ({
    type: actionTypes.REMOVE_OFF_GRAPH_SIGNAL,
    key
});

const setSelectedAssetRaw = (asset) => ({
    type: actionTypes.SET_SELECTED_ASSET,
    asset
});

const setBrowserCategoryRaw = (category) => ({
    type: actionTypes.SET_BROWSER_CATEGORY,
    category
});

const setSelectedPowertrainIdRaw = (powertrainId) => ({
    type: actionTypes.SET_SELECTED_POWERTRAIN_ID,
    powertrainId
});

const resolvePowertrainId = (powertrain) =>
    powertrain?.powertrainId ?? powertrain?.systemId ?? powertrain?.id ?? null;

const resolveAssetId = (asset) => asset?.id ?? asset?.assetId;

// True if the given powertrain's own assets list contains the asset (by id).
const powertrainContainsAsset = (powertrain, asset) => {
    const targetId = String(resolveAssetId(asset) ?? '');
    if (!targetId) { return false; }
    return (powertrain?.assets || []).some(a => {
        const id = String(a?.assetId ?? a?.id ?? '');
        return id && id === targetId;
    });
};

const findPowertrainById = (powertrains, powertrainId) => {
    if (!powertrains?.length || powertrainId === null || powertrainId === undefined) { return null; }
    return powertrains.find(pt => resolvePowertrainId(pt)?.toString() === powertrainId?.toString()) || null;
};

// Returns the first powertrain containing the asset — prefer powertrainContainsAsset to keep the current one when valid.
const findPowertrainForAsset = (powertrains, asset) => {
    if (!powertrains?.length || !asset) { return null; }
    return powertrains.find(pt => powertrainContainsAsset(pt, asset)) || null;
};

const getSelectedAssetIdFromState = (state) => resolveAssetId(state?.crossAssetAnalysisV2?.selectedAsset);

const isSameAssetId = (firstAssetId, secondAssetId) => {
    if (firstAssetId === null || firstAssetId === undefined) { return false; }
    if (secondAssetId === null || secondAssetId === undefined) { return false; }
    return String(firstAssetId) === String(secondAssetId);
};

const shouldApplyScopedKPIResponse = (getState, requestAssetId, requestTab) => {
    const state = getState();
    const selectedAssetId = getSelectedAssetIdFromState(state);
    const activeTab = getCrossAssetAnalysisActiveTab(state);
    return isSameAssetId(selectedAssetId, requestAssetId) && activeTab === requestTab;
};

export const clearStore = () => ({ type: actionTypes.CLEAR_STORE });

// Resets store state but keeps offGraphSignals so the user finds their previous signals on return
export const onGraphClearStore = () => ({ type: actionTypes.ON_GRAPH_CLEAR_STORE });

// Moves every ON GRAPH signal into OFF GRAPH (called on tab switch and page navigation)
export const saveOnGraphToOffGraph = () => (dispatch, getState) => {
    const state = getState();
    const chartData = state?.chartView?.chartData || [];
    const activeTab = getCrossAssetAnalysisActiveTab(state);
    const existingKeys = new Set(
        (state?.crossAssetAnalysisV2?.offGraphSignals || []).map(s => s.key)
    );

    if (!chartData.length || !activeTab) { return; }

    chartData.forEach(item => {
        const assetOrComponent = item?.asset || item?.component;
        if (!item?.kpi || !assetOrComponent) { return; }

        const assetId = assetOrComponent?.id ?? assetOrComponent?.assetId ?? item?.kpi?.assetId;
        if (!assetId) { return; }

        const kpi = item.kpi;
        const keyByTab = activeTab === opButtons.histogramV2
            ? kpi?.histogramKey ?? kpi?.measurementTypeIdentifier
            : activeTab === opButtons.scatterPlotV2
                ? kpi?.scatterKey ?? kpi?.measurementTypeIdentifier
                : kpi?.timeseriesKey ?? kpi?.measurementTypeKey ?? kpi?.timeseriesNameTextKey;

        if (!keyByTab) { return; }
        const key = `${activeTab}|${String(assetId)}|${keyByTab}`;

        if (!existingKeys.has(key)) {
            dispatch(addOffGraphSignal({ key, kpi, asset: assetOrComponent }));
            existingKeys.add(key);
        }
    });
};

export const setBrowserCategory = (category) => (dispatch) => {
    dispatch(setBrowserCategoryRaw(category));
};

export const setSelectedPowertrainId = (powertrainId) => (dispatch) => {
    dispatch(setSelectedPowertrainIdRaw(powertrainId));
};

export const loadPowertrainListForBrowser = (organizationId) => async (dispatch, getState) => {
    if (!organizationId) { return; }

    const state = getState();
    const selectedFilters = state?.generalFilter?.selectedFilters || {};
    const filtersWithOrganization = selectedFilters?.organizations?.length
        ? selectedFilters
        : {
            ...selectedFilters,
            organizations: [{ organizationID: organizationId }]
        };

    dispatch(getPowertrainListForBrowserAction(apiCallStatus.LOADING));
    try {
        const request = formatConditionIndexOverviewRequest(
            1,
            filtersWithOrganization,
            '',
            [],
            '',
            [],
            '',
            false
        );
        const response = await ConditionIndicesApi.getPowertrainOverview(request);
        const powertrains = response?.powertrains || [];

        dispatch(getPowertrainListForBrowserAction(apiCallStatus.SUCCESS, powertrains));

        const currentSelectedPowertrainId = getState()?.crossAssetAnalysisV2?.selectedPowertrainId;
        const currentSelectedPowertrain = findPowertrainById(powertrains, currentSelectedPowertrainId);
        const currentSelectedAsset = getState()?.crossAssetAnalysisV2?.selectedAsset;

        // Keep the current powertrain if still valid/containing the asset — avoids a same-id match elsewhere stealing the selection.
        if (currentSelectedPowertrain && (!currentSelectedAsset || powertrainContainsAsset(currentSelectedPowertrain, currentSelectedAsset))) {
            // no-op: current selection already valid
        } else {
            const matchingPowertrain = findPowertrainForAsset(powertrains, currentSelectedAsset);
            if (matchingPowertrain) {
                dispatch(setSelectedPowertrainIdRaw(resolvePowertrainId(matchingPowertrain)));
            } else if (!currentSelectedPowertrain) {
                const firstPowertrainId = resolvePowertrainId(powertrains?.[0]);
                dispatch(setSelectedPowertrainIdRaw(firstPowertrainId));
            }
        }

        const currentCategory = getState()?.crossAssetAnalysisV2?.selectedCategory;
        if (!currentCategory) {
            dispatch(setBrowserCategoryRaw(browserCategories.POWERTRAINS));
        }
    } catch (err) {
        dispatch(getPowertrainListForBrowserAction(apiCallStatus.ERROR, [], err));
    }
};

export const setSelectedAsset = (asset) => (dispatch) => {
    dispatch(clearKpiList());
    dispatch(setSelectedAssetRaw(asset));
};

// Selects the powertrain that contains the given asset (no-op if already correct or not found).
export const syncSelectedAssetToPowertrain = (asset) => (dispatch, getState) => {
    const state = getState();
    const powertrains = state?.crossAssetAnalysisV2?.powertrainList?.data || [];
    const targetAsset = asset || state?.crossAssetAnalysisV2?.selectedAsset;
    const currentId = state?.crossAssetAnalysisV2?.selectedPowertrainId;

    // Keep browsing the current powertrain if it already has this asset (avoids jumping on a duplicate/shared asset id elsewhere).
    const currentPowertrain = findPowertrainById(powertrains, currentId);
    if (currentPowertrain && powertrainContainsAsset(currentPowertrain, targetAsset)) {
        return;
    }

    const matchingPowertrain = findPowertrainForAsset(powertrains, targetAsset);
    if (!matchingPowertrain) { return; }
    const matchingId = resolvePowertrainId(matchingPowertrain);
    if (matchingId?.toString() !== currentId?.toString()) {
        dispatch(setSelectedPowertrainIdRaw(matchingId));
    }
};

// Loads signals (KPIs) for the given asset based on the active chart tab (defined early to avoid no-use-before-define).
const loadKPIsForAsset = (asset) => async (dispatch, getState) => {
    const requestAssetId = resolveAssetId(asset);
    if (!requestAssetId) { return; }
    const requestTab = getCrossAssetAnalysisActiveTab(getState());

    dispatch(getKPIsAction(apiCallStatus.LOADING));
    try {
        if (requestTab === opButtons.histogramV2) {
            const data = await AnalyticsApi.getHistogramV2Config(requestAssetId);
            if (shouldApplyScopedKPIResponse(getState, requestAssetId, requestTab)) {
                dispatch(getKPIsAction(apiCallStatus.SUCCESS, data?.histograms || []));
            }
        } else if (requestTab === opButtons.scatterPlotV2) {
            const data = await AnalyticsApi.getScatterV2Config(requestAssetId);
            if (shouldApplyScopedKPIResponse(getState, requestAssetId, requestTab)) {
                dispatch(getKPIsAction(apiCallStatus.SUCCESS, data || []));
            }
        } else {
            const data = await TimeSeriesApi.getMeasurementTypes(requestAssetId);
            if (shouldApplyScopedKPIResponse(getState, requestAssetId, requestTab)) {
                const timeSeries = (data || []).map((value) => value.timeseries.flat());
                dispatch(getKPIsAction(apiCallStatus.SUCCESS, timeSeries.flat()));
            }
        }
    } catch (err) {
        if (shouldApplyScopedKPIResponse(getState, requestAssetId, requestTab)) {
            dispatch(getKPIsAction(apiCallStatus.ERROR, null, err));
        }
    }
};

// Fetches subscription/contract info for the asset, needed to detect no-subscription assets (mirrors V1's sidepanel behavior).
const loadSubscriptionInfo = (asset) => async (dispatch, getState) => {
    const requestAssetId = resolveAssetId(asset);
    if (!requestAssetId) { return; }

    dispatch(getSubscriptionAction(apiCallStatus.LOADING));
    try {
        const assetIdParams = { assetIds: [requestAssetId], type: null, statusCodes: [], maxEndDate: null };
        const data = await SubscriptionInfoApi.getSubscriptionList(assetIdParams);
        const selectedAssetId = getSelectedAssetIdFromState(getState());

        if (!isSameAssetId(selectedAssetId, requestAssetId)) {
            return;
        }

        dispatch(getSubscriptionAction(apiCallStatus.SUCCESS, data));
    } catch (err) {
        const selectedAssetId = getSelectedAssetIdFromState(getState());
        if (isSameAssetId(selectedAssetId, requestAssetId)) {
            dispatch(getSubscriptionAction(apiCallStatus.ERROR, null, err));
        }
    }
};

// Fetches asset capabilities then triggers KPI load for the active chart tab.
export const loadCapabilityAndKPIs = (asset) => async (dispatch, getState) => {
    const requestAssetId = resolveAssetId(asset);
    if (!requestAssetId) { return; }

    dispatch(getCapabilityAction(apiCallStatus.LOADING));
    dispatch(loadSubscriptionInfo(asset));
    try {
        const { generalFilter } = getState();
        const organizationId = generalFilter?.selectedFilters?.organizations?.[0]?.organizationID;
        const capabilityData = await CapabilityApi.getCapabilities(organizationId, [requestAssetId], []);
        const selectedAssetId = getSelectedAssetIdFromState(getState());

        if (!isSameAssetId(selectedAssetId, requestAssetId)) {
            return;
        }

        dispatch(getCapabilityAction(apiCallStatus.SUCCESS, capabilityData));
        dispatch(loadKPIsForAsset(asset));
    } catch (err) {
        const selectedAssetId = getSelectedAssetIdFromState(getState());
        if (isSameAssetId(selectedAssetId, requestAssetId)) {
            dispatch(getCapabilityAction(apiCallStatus.ERROR, null, err));
        }
    }
};

// Re-fetches KPIs when the chart tab changes (Trend ↔ Histogram ↔ Scatter).
export const reloadKPIsOnTabChange = () => (dispatch, getState) => {
    const { crossAssetAnalysisV2 } = getState();
    const { selectedAsset } = crossAssetAnalysisV2;
    // Off-graph signals are NOT cleared here — preserved across tab switches so the user sees them again on return.
    if (selectedAsset) {
        dispatch(clearKpiList());
        dispatch(loadKPIsForAsset(selectedAsset));
    }
};

